import { useState } from 'react';

import {
  pendingDeclines,
  type DeclineCallbacks,
} from './pending-declines.store';

import type { PendingEntry } from './connection-actions';

/** Where a line of the requests card stands:
 * - `waiting`: nothing has been done with it
 * - `sending`: the user answered, and the server has not yet. The line stays, with its buttons.
 * - `connected`: the server took the accept. The line says so for a moment, then closes.
 * - `closing`: the line is going away, because the request was declined or cancelled
 * - `closed`: the line has closed. It stays closed.
 * A failed answer puts the line back to `waiting`. */
export type LineStatus =
  | 'waiting'
  | 'sending'
  | 'connected'
  | 'closing'
  | 'closed';

type Answered = Exclude<LineStatus, 'waiting'>;

/** The lines that were answered, and the order of all lines when the first one was answered. The lists refresh when an
 * answer goes through and would take its line away at once, so the round keeps each answered line until it has closed.
 * The lines that wait come from the lists, as they are now. */
type Round = {
  order: string[];
  answered: ReadonlyMap<string, { entry: PendingEntry; status: Answered }>;
};

/** Whether the line is out of sight or going out of it */
export const isCollapsed = (status: LineStatus): boolean =>
  status === 'connected' || status === 'closing' || status === 'closed';

/** Drops the closed lines of the round that have nothing in flight. The lists no longer hold a line that was accepted
 * or cancelled, or whose decline was sent, and a request that comes from its account later is a new line. A line whose
 * decline waits stays, because the lists still hold its request. */
const dropSettled = (round: Round | null): Round | null => {
  if (!round) return null;
  const answered = new Map(
    [...round.answered].filter(
      ([accountId, { status }]) =>
        status !== 'closed' || pendingDeclines.has(accountId)
    )
  );
  return answered.size === 0 ? null : { ...round, answered };
};

/** The lines of the requests card, and the one place that tracks each line from waiting to closed. The card owns it
 * and the rows read their status from it and call it. `pending` are the entries that the lists hold. */
export const useRequestLines = (pending: PendingEntry[]) => {
  const [round, setRound] = useState<Round | null>(null);

  // A request that is declined stays hidden while its decline waits, whichever screen declined it
  const fresh = pending.filter(
    ({ accountId }) => !pendingDeclines.has(accountId)
  );
  const entries = !round
    ? fresh
    : [
        ...round.order.flatMap(
          (accountId) =>
            round.answered.get(accountId)?.entry ??
            fresh.find((other) => other.accountId === accountId) ??
            []
        ),
        // Requests that arrive in the meantime follow
        ...fresh.filter(({ accountId }) => !round.order.includes(accountId)),
      ];

  const statusOf = (accountId: string): LineStatus =>
    round?.answered.get(accountId)?.status ?? 'waiting';

  const setStatus = (accountId: string, status: Answered | null) =>
    setRound((current) => {
      const answered = new Map(current?.answered);
      const entry =
        answered.get(accountId)?.entry ??
        entries.find((other) => other.accountId === accountId);
      if (status && entry) answered.set(accountId, { entry, status });
      else answered.delete(accountId);
      if (answered.size === 0) return null;
      const order = current?.order ?? entries.map((other) => other.accountId);
      return {
        order: order.includes(accountId) ? order : [...order, accountId],
        answered,
      };
    });

  return {
    entries,
    statusOf,
    /** Runs `run`, which says whether the answer went through. The line goes to `done` when it did, and back to waiting
     * when it did not. */
    answer: async (
      accountId: string,
      run: () => Promise<boolean>,
      done: 'connected' | 'closing'
    ): Promise<boolean> => {
      setStatus(accountId, 'sending');
      const didAnswer = await run();
      setStatus(accountId, didAnswer ? done : null);
      return didAnswer;
    },
    /** Closes the line and starts the decline, which Undo can take back. `start` gets what the decline reports back. */
    decline: (
      accountId: string,
      start: (callbacks: DeclineCallbacks) => void
    ) => {
      setStatus(accountId, 'closing');
      start({
        onRestore: () => setStatus(accountId, null),
        onSent: () => setRound(dropSettled),
      });
    },
    /** The line has finished closing. The last line stays closed with the card, which still shows it while it fades. */
    close: (accountId: string) => {
      setStatus(accountId, 'closed');
      const hasOpenLine = entries.some(
        (other) =>
          other.accountId !== accountId &&
          statusOf(other.accountId) !== 'closed'
      );
      if (hasOpenLine) setRound(dropSettled);
    },
    /** The card has finished closing. The lines whose decline waits out its Undo stay, because Undo can bring one back,
     * and the round ends when the last decline is sent. */
    reset: () => setRound(dropSettled),
  };
};

export type RequestLines = ReturnType<typeof useRequestLines>;
